/**
 * The controls both 3D viewers offer.
 *
 * Deliberately six, not a CAD ribbon. A customer looking at their own
 * reconstruction has exactly these questions: where is it, how do I get back to
 * it, how do I get closer, and can I see it bigger.
 *
 * Every control has a real accessible name and a visible label or a tooltip -
 * the previous toolbar was one button plus a sentence, and the sentence was the
 * only place the controls were explained at all. Icon-only buttons are limited
 * to the ones whose meaning is universal, and even those carry a name.
 */

import type { ReactNode } from "react";
import { VIEW_PRESETS } from "./viewerPresets";
import type { ViewMode } from "./ModelViewer";

/** The customer words. "Doku" is the real appearance; the rest replace it. */
const MODE_LABELS: Record<ViewMode, string> = {
  texture: "Doku",
  solid: "Tek renk",
  light: "Açık gri",
  dark: "Koyu gri",
  // Not a shading option like the three above it, and named so. The others
  // change how the surface looks; this one changes what is being shown - where
  // each part of the model came from - and it has a legend of its own.
  trust: "Geometri güveni",
};
const MODE_CHOICES: ViewMode[] = ["texture", "solid", "light", "dark", "trust"];

export function ViewerToolbar({
  onFit,
  onReset,
  onZoomIn,
  onZoomOut,
  expanded,
  onToggleExpanded,
  onCapture,
  capturing,
  captureFailed,
  caption,
  hint,
  extra,
  onPreset,
  mode,
  onMode,
  textured,
}: {
  onFit: () => void;
  onReset: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  expanded: boolean;
  onToggleExpanded: () => void;
  /** Save what the canvas is showing. Absent where the renderer cannot. */
  onCapture?: () => void;
  capturing?: boolean;
  /** Set when a capture was attempted and produced nothing. */
  captureFailed?: boolean;
  /** What is being looked at, where that is not obvious from the picture. */
  caption?: string;
  /** One line describing the gestures that actually work here. */
  hint: string;
  /** Anything only one of the two viewers has - the point-size control. */
  extra?: ReactNode;
  /**
   * Jump to an axis view. Absent on the point cloud, which has no surface to
   * look at squarely.
   */
  onPreset?: (theta: number, phi: number) => void;
  /** How the surface is shaded. Absent on the point cloud, which has none. */
  mode?: ViewMode;
  onMode?: (mode: ViewMode) => void;
  /**
   * Whether this model has an atlas. Only changes what the first option is
   * *called*: on a textured model it is the photograph, on an untextured one
   * it is the vertex colour, and both are that model's real appearance.
   * Labelling the second "Doku" would name a file that does not exist.
   */
  textured?: boolean;
}) {
  return (
    <div className="viewer__controls">
      <div className="viewer__tools" role="group" aria-label="3D görünüm kontrolleri">
        <button type="button" className="btn btn--secondary" onClick={onFit}>
          Modele sığdır
        </button>
        <button type="button" className="btn btn--secondary" onClick={onReset}>
          Görünümü sıfırla
        </button>
        {/*
          Axis views, named for where the camera goes rather than for a side of
          the object. A reconstruction has no idea which of its faces is the
          front, and a button saying so would be asserting it.
        */}
        {onPreset && (
          <span className="viewer__presets" role="group" aria-label="Hazır görünümler">
            {VIEW_PRESETS.map((preset) => (
              <button
                key={preset.key}
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={() => onPreset(preset.theta, preset.phi)}
              >
                {preset.label}
              </button>
            ))}
          </span>
        )}
        {/*
          How the surface is shaded. A flat colour is how somebody checks the
          *form* of a model without the photograph on it distracting from a
          dent - and it is a change to this frame, never to the file.
        */}
        {mode && onMode && (
          <label className="inline-field">
            <span>Görünüm</span>
            <select
              value={mode}
              onChange={(event) => onMode(event.target.value as ViewMode)}
              aria-label="Yüzey görünümü"
            >
              {MODE_CHOICES.map((choice) => (
                <option key={choice} value={choice}>
                  {choice === "texture" && !textured
                    ? "Yüzey rengi"
                    : MODE_LABELS[choice]}
                </option>
              ))}
            </select>
          </label>
        )}
        {/*
          A wheel is not the only way in. These are what a laptop trackpad, a
          touchscreen and a keyboard user have.
        */}
        <button
          type="button"
          className="btn btn--secondary viewer__icon"
          onClick={onZoomIn}
          title="Yakınlaştır"
          aria-label="Yakınlaştır"
        >
          <span aria-hidden="true">+</span>
        </button>
        <button
          type="button"
          className="btn btn--secondary viewer__icon"
          onClick={onZoomOut}
          title="Uzaklaştır"
          aria-label="Uzaklaştır"
        >
          <span aria-hidden="true">−</span>
        </button>
        <button
          type="button"
          className="btn btn--secondary"
          onClick={onToggleExpanded}
          aria-pressed={expanded}
        >
          {expanded ? "Küçült" : "Tam ekran"}
        </button>
        {/*
          The canvas as it stands, with none of the interface around it. Only
          offered where the renderer can actually read its own buffer back.
        */}
        {onCapture && (
          <button
            type="button"
            className="btn btn--secondary"
            onClick={onCapture}
            disabled={capturing}
          >
            {capturing ? "Kaydediliyor…" : "Ekran görüntüsü al"}
          </button>
        )}
        {extra}
      </div>
      {/*
        A sparse cloud and a surface model look alike to somebody who has not
        been told they are different things, and this viewer draws one of them.
      */}
      {caption && <span className="viewer__caption">{caption}</span>}
      {captureFailed && (
        <span className="muted" role="alert">
          Ekran görüntüsü alınamadı. Görünümü yeniden çizip tekrar deneyebilirsiniz.
        </span>
      )}
      <span className="viewer__hint">{hint}</span>
    </div>
  );
}

/** The gestures that genuinely work, named once for both viewers. */
export const VIEWER_HINT =
  "Sürükle: döndür · Tekerlek veya iki parmak: yakınlaştır · Sağ tık, Shift+sürükle veya iki parmak: taşı";
